import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Fetch investor overview data
export const investorOverviewData = createAsyncThunk(
  "api/investorOverviewData",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/investors/get-latest-investor-details`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch investor report data
export const investorReportData = createAsyncThunk(
  "api/investorReportData",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/investors/get-investor-reports?financial_year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch News and Notifications
export const investorNewsAndNotificationsData = createAsyncThunk(
  "api/investorNewsAndNotificationsData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/investors/get-news-notifications`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch Stock Exchange
export const investorStockAndExchangeData = createAsyncThunk(
  "api/investorStockAndExchangeData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/investors/get-stock-notifications`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch Conference Data
export const investorConferenceData = createAsyncThunk(
  "api/investorConferenceData",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/conferences/getlist?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch KYC Data
export const investorShareholderKYCData = createAsyncThunk(
  "api/investorShareholderKYCData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shareholders/get-kyc-files`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// IEPF Data
export const investorShareholderIEPFData = createAsyncThunk(
  "api/investorShareholderIEPFData",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shareholders/get-form-iepf-files?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// IEPF-2 Data
export const investorShareholderIEPF_2_Data = createAsyncThunk(
  "api/investorShareholderIEPF_2_Data",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shareholders/get-form-iepf-2-files?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// IEPF-7 Data
export const investorShareholderIEPF_7_Data = createAsyncThunk(
  "api/investorShareholderIEPF_7_Data",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shareholders/get-form-iepf-7-files?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// IEPF-4 Data
export const investorShareholderIEPF_4_Data = createAsyncThunk(
  "api/investorShareholderIEPF_4_Data",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/shareholders/get-form-iepf-4-files?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

//Presentation Data
export const investorPresentationData = createAsyncThunk(
  "api/investorPresentationData",
  async (FinancialYear, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/investor-presentations/getlist?year=${FinancialYear}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const apiMediaSlice = createSlice({
  name: "api",
  initialState: {
    overviewData: null, // Data from investorOverviewData
    reportData: null, // Data from investorReportData
    loading: false,
    error: null,
    newsAndNotificationsData: null,
    stockAndExchangeData: null,
    conferenceData: null,
    shareholderKYCData: null,
    shareholderIEPF_2_Data: null,
    shareholderIEPF_7_Data: null,
    shareholderIEPF_4_Data: null,
    shareholderIEPFData: null,
    presentationData: null,
  },
  extraReducers: (builder) => {
    builder
      // investorOverviewData cases
      .addCase(investorOverviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.overviewData = action.payload;
      })
      .addCase(investorOverviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // investorReportData cases
      .addCase(investorReportData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorReportData.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(investorReportData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //News And Notifications
      .addCase(investorNewsAndNotificationsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorNewsAndNotificationsData.fulfilled, (state, action) => {
        state.loading = false;
        state.newsAndNotificationsData = action.payload;
      })
      .addCase(investorNewsAndNotificationsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Stock And Exchange
      .addCase(investorStockAndExchangeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorStockAndExchangeData.fulfilled, (state, action) => {
        state.loading = false;
        state.stockAndExchangeData = action.payload;
      })
      .addCase(investorStockAndExchangeData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //Conference Data
      .addCase(investorConferenceData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorConferenceData.fulfilled, (state, action) => {
        state.loading = false;
        state.conferenceData = action.payload;
      })
      .addCase(investorConferenceData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //KYC Data
      .addCase(investorShareholderKYCData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorShareholderKYCData.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderKYCData = action.payload;
      })
      .addCase(investorShareholderKYCData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //IEPF Data
      .addCase(investorShareholderIEPFData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorShareholderIEPFData.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderIEPFData = action.payload;
      })
      .addCase(investorShareholderIEPFData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //IEPF - 2 Data
      .addCase(investorShareholderIEPF_2_Data.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorShareholderIEPF_2_Data.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderIEPF_2_Data = action.payload;
      })
      .addCase(investorShareholderIEPF_2_Data.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //IEPF - 7 Data
      .addCase(investorShareholderIEPF_7_Data.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorShareholderIEPF_7_Data.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderIEPF_7_Data = action.payload;
      })
      .addCase(investorShareholderIEPF_7_Data.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //IEPF - 4 Data
      .addCase(investorShareholderIEPF_4_Data.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorShareholderIEPF_4_Data.fulfilled, (state, action) => {
        state.loading = false;
        state.shareholderIEPF_4_Data = action.payload;
      })
      .addCase(investorShareholderIEPF_4_Data.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Presentation Data
      .addCase(investorPresentationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(investorPresentationData.fulfilled, (state, action) => {
        state.loading = false;
        state.presentationData = action.payload;
      })
      .addCase(investorPresentationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default apiMediaSlice.reducer;
